import { BaseCallbackHandler } from '@langchain/core/callbacks/base';
import type { LLMResult } from '@langchain/core/outputs';
import { AIMessage, BaseMessage, ToolMessage, isAIMessage } from '@langchain/core/messages';

export const OUTPUT_LIMIT_NOTICE = 'The model reached its output limit before answering. Try again or narrow the request.';
export const NO_RESPONSE_NOTICE = 'No response received.';
export const INCOMPLETE_RESPONSE_NOTICE = 'The response was incomplete. Try again.';
export const MALFORMED_TOOL_CALL_NOTICE = 'The model produced an invalid tool call. Try again.';
export const STUCK_CONVERSATION_NOTICE = 'This conversation may be stuck. Start a new conversation to continue.';

export type TurnSkipReason = 'empty' | 'length' | 'invalid_tool_calls' | 'no_finish_reason';

export type TurnDecision = {
  readonly persisted: boolean;
  readonly skipReason: TurnSkipReason | null;
  readonly notice: string | null;
};

export type TelemetrySource = 'orchestrator' | 'subagent' | 'extraction';

type ContentBlock = { type?: unknown, text?: unknown, reasoning?: unknown, thinking?: unknown, signature?: unknown };

export const isTextPart = (part: unknown): part is { text: string } => (
  typeof part === 'object'
  && part !== null
  && 'type' in part
  && part.type === 'text'
  && 'text' in part
  && typeof part.text === 'string'
);

export const contentToText = (content: unknown): string => {
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) return content.filter(isTextPart).map((part) => part.text).join('\n');
  return '';
};

export const readFinishReason = (metadata: unknown): unknown => (
  typeof metadata === 'object' && metadata !== null && 'finish_reason' in metadata
    ? metadata.finish_reason
    : undefined
);

const contentBlocks = (content: unknown): ContentBlock[] => (
  Array.isArray(content) ? content.filter((b): b is ContentBlock => typeof b === 'object' && b !== null) : []
);

const hasText = (message: BaseMessage): boolean => contentToText(message.content).trim().length > 0;

const hasToolCalls = (message: BaseMessage): boolean => (
  'tool_calls' in message && Array.isArray(message.tool_calls) && message.tool_calls.length > 0
);

const hasInvalidToolCalls = (message: BaseMessage): boolean => (
  'invalid_tool_calls' in message && Array.isArray(message.invalid_tool_calls) && message.invalid_tool_calls.length > 0
);

/**
 * Decides whether a finished model call may be checkpointed.
 * Shared by the save guard (middleware) and telemetry so both always agree.
 */
export const classifyModelTurn = (message: BaseMessage, finishReason: unknown): TurnDecision => {
  if (hasInvalidToolCalls(message)) {
    const notice = finishReason === 'length' ? OUTPUT_LIMIT_NOTICE : MALFORMED_TOOL_CALL_NOTICE;
    return { persisted: false, skipReason: 'invalid_tool_calls', notice };
  }

  if (typeof finishReason !== 'string' || finishReason === '') {
    return { persisted: false, skipReason: 'no_finish_reason', notice: INCOMPLETE_RESPONSE_NOTICE };
  }

  if (finishReason === 'length') {
    return { persisted: false, skipReason: 'length', notice: OUTPUT_LIMIT_NOTICE };
  }

  if (hasToolCalls(message) || hasText(message)) {
    return { persisted: true, skipReason: null, notice: null };
  }

  return { persisted: false, skipReason: 'empty', notice: NO_RESPONSE_NOTICE };
};

/**
 * History entries that must never be sent back to the model: assistant turns
 * with no text and no tool calls, or with cut-off (invalid) tool calls.
 * Does not look at finish_reason, so legacy turns without it are kept when they have text.
 */
export const isUnsavableHistoryTurn = (message: BaseMessage): boolean => {
  if (!isAIMessage(message)) return false;
  if (hasInvalidToolCalls(message)) return true;
  return !hasToolCalls(message) && !hasText(message);
};

const hasRole = (message: BaseMessage): boolean => (
  'role' in message && typeof message.role === 'string' && message.role !== ''
);

export const isRolelessMessage = (message: BaseMessage): boolean => (
  message._getType() === 'generic'
  && !hasRole(message)
  && !('tool_call_id' in message && message.tool_call_id)
  && !('name' in message && message.name)
);

export const toAssistantTurn = (message: BaseMessage): AIMessage => new AIMessage({
  id: message.id,
  content: message.content,
  response_metadata: message.response_metadata,
});

const isToolResult = (message: BaseMessage): message is ToolMessage => message._getType() === 'tool';

const followingToolResultIds = (messages: BaseMessage[], index: number): Set<string> => {
  const ids = new Set<string>();
  for (let next = index + 1; next < messages.length; next += 1) {
    const candidate = messages[next];
    if (!isToolResult(candidate)) break;
    ids.add(candidate.tool_call_id);
  }
  return ids;
};

const withoutToolCallPayload = (kwargs: Record<string, unknown> = {}): Record<string, unknown> => {
  const { tool_calls: toolCalls, function_call: functionCall, ...rest } = kwargs;
  return rest;
};

/**
 * Repairs history shapes that providers reject before each model call. Never mutates the checkpoint.
 * - role-less messages (Bedrock streaming deltas) become assistant messages, matching how invoke() shows them
 * - assistant tool calls without a ToolMessage in the run right after them are dropped; a turn left empty is dropped too
 * - ToolMessages are kept only when the assistant turn directly before them made that call
 * - empty and invalid assistant turns are dropped
 */
export const sanitizeHistory = (messages: BaseMessage[]): BaseMessage[] => {
  const repaired: BaseMessage[] = [];
  let answerable = new Set<string>();

  messages.forEach((message, index) => {
    if (isToolResult(message)) {
      if (answerable.has(message.tool_call_id)) repaired.push(message);
      return;
    }
    answerable = new Set<string>();

    if (isRolelessMessage(message)) {
      const assistant = toAssistantTurn(message);
      if (!isUnsavableHistoryTurn(assistant)) repaired.push(assistant);
      return;
    }

    if (isUnsavableHistoryTurn(message)) return;

    if (!isAIMessage(message) || !hasToolCalls(message)) {
      repaired.push(message);
      return;
    }

    const results = followingToolResultIds(messages, index);
    const answeredCalls = (message.tool_calls ?? []).filter((call) => Boolean(call.id) && results.has(call.id as string));
    answerable = new Set(answeredCalls.map((call) => call.id as string));

    if (answeredCalls.length === message.tool_calls?.length) {
      repaired.push(message);
      return;
    }

    if (answeredCalls.length === 0 && !hasText(message)) return;

    repaired.push(new AIMessage({
      id: message.id,
      content: message.content,
      tool_calls: answeredCalls,
      additional_kwargs: withoutToolCallPayload(message.additional_kwargs),
      response_metadata: message.response_metadata,
      usage_metadata: message.usage_metadata,
      name: message.name,
    }));
  });

  return repaired;
};

const reasoningText = (block: ContentBlock): string => {
  if (typeof block.reasoning === 'string') return block.reasoning;
  if (typeof block.thinking === 'string') return block.thinking;
  return '';
};

const countReasoning = (message: AIMessage): number => {
  const fromBlocks = contentBlocks(message.content)
    .filter((b) => b.type === 'reasoning' || b.type === 'thinking')
    .reduce((total, b) => total + reasoningText(b).length, 0);
  const reasoningContent = message.additional_kwargs?.reasoning_content;
  const fromKwargs = typeof reasoningContent === 'string' ? reasoningContent.length : 0;
  return fromBlocks + fromKwargs;
};

const hasReasoningSignature = (message: AIMessage): boolean => {
  const meta = message.response_metadata ?? {};
  const kwargs = message.additional_kwargs ?? {};
  return Boolean(
    meta.reasoning_signature
    || kwargs.reasoning_signature
    || contentBlocks(message.content).some((b) => b.signature !== undefined)
  );
};

export type ModelCallTelemetry = {
  readonly source: TelemetrySource,
  readonly response_id: string | null,
  readonly finish_reason: string | null,
  readonly content_length: number,
  readonly tool_call_count: number,
  readonly invalid_tool_call_count: number,
  readonly reasoning_length: number,
  readonly has_reasoning_signature: boolean,
  readonly http_status: number | null,
  readonly error_message: string | null,
  readonly persisted: boolean | null,
  readonly skip_reason: TurnSkipReason | null,
};

const TELEMETRY_TAG = '[LlmProxyService][model-call]';

const emitTelemetry = (record: ModelCallTelemetry): void => {
  // eslint-disable-next-line no-console
  console.info(TELEMETRY_TAG, record);
};

/**
 * Records one structured entry per model call. Attach to ChatOpenAI via `callbacks`.
 * `persisted` / `skip_reason` describe the save-guard decision for that call.
 */
export class ModelCallTelemetryHandler extends BaseCallbackHandler {
  name = 'model_call_telemetry';

  private readonly source: TelemetrySource;

  constructor(source: TelemetrySource) {
    super();
    this.source = source;
  }

  handleLLMEnd(output: LLMResult, runId: string): void {
    const generation = output.generations?.[0]?.[0] as { message?: AIMessage, generationInfo?: Record<string, unknown> } | undefined;
    const message = generation?.message;
    if (!message) return;

    const finishReason = readFinishReason(message.response_metadata) ?? generation?.generationInfo?.finish_reason;
    const decision = classifyModelTurn(message, finishReason);
    const toolCalls = 'tool_calls' in message && Array.isArray(message.tool_calls) ? message.tool_calls.length : 0;
    const invalidToolCalls = 'invalid_tool_calls' in message && Array.isArray(message.invalid_tool_calls)
      ? message.invalid_tool_calls.length
      : 0;

    emitTelemetry({
      source: this.source,
      response_id: message.id ?? null,
      finish_reason: typeof finishReason === 'string' ? finishReason : null,
      content_length: contentToText(message.content).length,
      tool_call_count: toolCalls,
      invalid_tool_call_count: invalidToolCalls,
      reasoning_length: countReasoning(message),
      has_reasoning_signature: hasReasoningSignature(message),
      http_status: null,
      error_message: null,
      persisted: decision.persisted,
      skip_reason: decision.skipReason,
    });
  }

  handleLLMError(error: Error, runId: string): void {
    const { status } = error as { status?: unknown };
    emitTelemetry({
      source: this.source,
      response_id: null,
      finish_reason: null,
      content_length: 0,
      tool_call_count: 0,
      invalid_tool_call_count: 0,
      reasoning_length: 0,
      has_reasoning_signature: false,
      http_status: typeof status === 'number' ? status : null,
      error_message: error.message || null,
      persisted: false,
      skip_reason: null,
    });
  }
}
